import React, { useEffect, useMemo, useState } from 'react';
import { Btn, Chip, DataTable, Loading, Msg, Section, Select, TextInput, fmtDate, useAdminGet } from '../kit';
import { Pager, num, pageInfo, rowsOf, str, useDebounced } from '../ipintel/shared';
import PageDrawer, { type PageRef } from './PageDrawer';
import { HorizonBadge } from '../horizon/shared';
import {
  CTAS, CTA_LABELS, EXPORT_PATH, ExcelButton, KNOWN_INDUSTRIES, OpenLink, type Range, S, SECTIONS, SECTION_LABELS,
  SHARE_LABELS, SOURCES, SOURCE_LABELS, STATES, STATE_LABELS, fmtInt, fmtSeconds, nameOf, rangeParams,
} from './shared';

/**
 * Tech pages / University pages — GET tech_pages/pages {kind, filters…}: one
 * row per public page with every metric the page records (views, visitors,
 * time, per-section views, CTA clicks, locked-section clicks, shares,
 * sign-ups, crawler hits). Filtering, sorting and paging run on the server;
 * a row opens the page's drawer (page_detail). The Excel export takes the
 * same filters. A tech page's Horizon column (row.horizon: {status, horizon,
 * levers_count}) links to that technology in the Horizon data admin.
 */

type Uni = { code: string; name: string; label: string; country: string };

type Filters = {
  q: string; university: string; industry: string; country: string; trl: string;
  state: string; source: string; has_traffic: string;
};
const EMPTY: Filters = { q: '', university: '', industry: '', country: '', trl: '', state: '', source: '', has_traffic: '' };

/** Nested metric maps on a row → flat columns (prefix, server sort key, label, known ids). */
const GROUPS = [
  { key: 'section_views', prefix: 'sv__', title: 'Section views', label: 'Seen', names: SECTION_LABELS, known: [...SECTIONS] as string[] },
  { key: 'cta_clicks', prefix: 'cta__', title: 'CTA clicks', label: 'CTA', names: CTA_LABELS, known: [...CTAS] as string[] },
  { key: 'locked_clicks', prefix: 'lk__', title: 'Locked clicks', label: 'Locked', names: SECTION_LABELS, known: ['summary', 'researchers_ip', 'problem_applications'] as string[] },
  { key: 'share', prefix: 'sh__', title: 'Shares', label: 'Share', names: SHARE_LABELS, known: Object.keys(SHARE_LABELS) as string[] },
] as const;

const TRL_OPTS = ['', '1', '2', '3', '4', '5', '6', '7', '8', '9'];
/** A select is as wide as its longest option; keep university names from pushing a phone sideways. */
const clip = (s: string, n: number) => (s.length > n ? `${s.slice(0, n - 1)}…` : s);

const PagesTable: React.FC<{
  kind: 'tech' | 'university'; range: Range; universities: Uni[];
}> = ({ kind, range, universities }) => {
  const isTech = kind === 'tech';
  const [f, setF] = useState<Filters>(EMPTY);
  const [sort, setSort] = useState<{ key: string; dir: 'asc' | 'desc' } | null>({ key: 'views', dir: 'desc' });
  const [page, setPage] = useState(0);
  const [perPage, setPerPage] = useState('50');
  const [tick, setTick] = useState(0);
  const [dlErr, setDlErr] = useState('');
  const [open, setOpen] = useState<PageRef | null>(null);
  const [show, setShow] = useState<Record<string, boolean>>(
    { section_views: true, cta_clicks: true, locked_clicks: true, share: true });
  const q = useDebounced(f.q);
  const industry = useDebounced(f.industry);
  const set = (k: keyof Filters) => (v: string) => setF(s => ({ ...s, [k]: v }));

  const sortParam = (key: string): string => {
    for (const g of GROUPS) if (key.startsWith(g.prefix)) return `${g.key}.${key.slice(g.prefix.length)}`;
    return key;
  };
  const filterParams = useMemo(() => {
    const p: Record<string, string | number> = {
      kind, q, university: f.university, country: f.country, state: f.state, source: f.source,
      has_traffic: f.has_traffic, ...rangeParams(range),
    };
    if (isTech) { p.industry = industry; p.trl = f.trl; }
    if (sort?.key) { p.sort = sortParam(sort.key); p.dir = sort.dir; }
    return p;
  }, [kind, q, industry, f.university, f.country, f.trl, f.state, f.source, f.has_traffic, range, sort, isTech]);
  const filterKey = JSON.stringify(filterParams);
  useEffect(() => { setPage(0); }, [filterKey, perPage]);

  const res = useAdminGet(S, 'pages', { ...filterParams, page, per_page: perPage }, [filterKey, page, perPage, tick]);
  const d = res.data || {};
  const raw = rowsOf(d, 'pages');
  const per = Number(perPage) || 50;
  const { total, pages } = pageInfo(d, raw, per);

  // Every nested key any row carries, known ids first in their page order.
  const dynCols = useMemo(() => GROUPS.map(g => {
    const seen = new Set<string>();
    raw.forEach((r: any) => { const m = r?.[g.key]; if (m && typeof m === 'object') Object.keys(m).forEach(k => seen.add(k)); });
    // A tech page always has the known sections / CTAs / channels, so they show
    // even at zero; a university page shows only what its rows actually carry.
    const extra = Array.from(seen).filter(k => !g.known.includes(k)).sort();
    const keys = isTech ? [...g.known, ...extra] : Array.from(seen).sort();
    return { ...g, keys };
  }), [raw, isTech]);

  const rows = useMemo(() => raw.map((r: any) => {
    const out: any = { ...r };
    GROUPS.forEach(g => {
      const m = r?.[g.key] || {};
      dynCols.find(x => x.key === g.key)!.keys.forEach(k => { out[`${g.prefix}${k}`] = num(m[k]); });
    });
    return out;
  }), [raw, dynCols]);

  const baseCols = isTech
    ? ['title', 'university_label', 'industry', 'trl', 'horizon', 'views', 'visitors', 'avg_seconds', 'signups', 'crawler_hits', 'last_view_at']
    : ['title', 'university_label', 'views', 'visitors', 'avg_seconds', 'signups', 'crawler_hits', 'last_view_at'];
  const columns = [...baseCols, ...dynCols.filter(g => show[g.key]).flatMap(g => g.keys.map(k => `${g.prefix}${k}`))];
  const labels: Record<string, string> = {
    title: isTech ? 'Technology' : 'University', university_label: isTech ? 'University' : 'Label',
    industry: 'Industry', trl: 'TRL', horizon: 'Horizon', views: 'Views', visitors: 'Visitors', avg_seconds: 'Avg. time',
    signups: 'Sign-ups', crawler_hits: 'Crawler hits', last_view_at: 'Last view',
  };
  dynCols.forEach(g => g.keys.forEach(k => { labels[`${g.prefix}${k}`] = `${g.label}: ${nameOf(k, g.names as Record<string, string>)}`; }));

  const countries = useMemo(() => Array.from(new Set(universities.map(u => u.country).filter(Boolean))).sort(), [universities]);
  const uniOpts = useMemo(() => [...universities].sort((a, b) => (a.label || a.name).localeCompare(b.label || b.name)), [universities]);
  const uniLabel = (code: string) => { const u = universities.find(x => x.code === code); return u ? (u.label || u.name) : code; };

  const chips: { label: string; value: string; clear: () => void }[] = [];
  if (f.q) chips.push({ label: 'Search', value: f.q, clear: () => set('q')('') });
  if (f.university) chips.push({ label: 'University', value: uniLabel(f.university), clear: () => set('university')('') });
  if (isTech && f.industry) chips.push({ label: 'Industry', value: f.industry, clear: () => set('industry')('') });
  if (f.country) chips.push({ label: 'Country', value: f.country, clear: () => set('country')('') });
  if (isTech && f.trl) chips.push({ label: 'TRL', value: f.trl, clear: () => set('trl')('') });
  if (f.state) chips.push({ label: 'Viewed as', value: STATE_LABELS[f.state] || f.state, clear: () => set('state')('') });
  if (f.source) chips.push({ label: 'Source', value: SOURCE_LABELS[f.source] || f.source, clear: () => set('source')('') });
  if (f.has_traffic) chips.push({ label: 'Traffic', value: f.has_traffic === '1' ? 'with views' : 'no views', clear: () => set('has_traffic')('') });

  const openRow = (r: any) => setOpen({ kind, target: str(r.target), path: str(r.path), title: str(r.title), row: r });
  return (
    <div className="flex flex-col gap-4">
      <Section title={isTech ? 'Tech pages' : 'University pages'}
        right={
          <div className="flex items-center gap-2 flex-wrap">
            <span className="text-xs text-grayscale-1100">{total.toLocaleString()} page{total === 1 ? '' : 's'}</span>
            {chips.length > 0 && <Btn small onClick={() => setF(EMPTY)}>Clear {chips.length} filter{chips.length === 1 ? '' : 's'}</Btn>}
            <Btn small onClick={() => setTick(t => t + 1)} disabled={res.loading}>Refresh</Btn>
            <ExcelButton path={EXPORT_PATH} params={filterParams} onError={setDlErr}
              fallback={`tech-pages-${kind}`} what={isTech ? 'These tech pages' : 'These university pages'} />
          </div>
        }>
        <p className="text-xs text-grayscale-1100 -mt-1">
          {isTech
            ? 'Every public technology page, with what visitors did on it in the period above. Click a title for its traffic, sections, CTAs, events, sessions, URL history and state previews.'
            : 'Every public university page (the list of that university\'s technologies). Click a name for its detail.'}
          {' '}Views and clicks come from the site's own tracking (site_events); crawler hits are counted server-side.
        </p>
        <div className="flex items-center gap-2 flex-wrap">
          <TextInput value={f.q} onChange={set('q')} wide
            placeholder={isTech ? 'Search title, path or tech id…' : 'Search name, label, path or code…'} />
          <Select value={f.university} onChange={set('university')}
            options={['', ...uniOpts.map(u => u.code)]}
            labels={{ '': 'All universities', ...Object.fromEntries(uniOpts.map(u => [u.code, clip(u.label || u.name, 28)])) }} />
          <Select value={f.country} onChange={set('country')} options={['', ...countries]} labels={{ '': 'All countries' }} />
          {isTech && (
            <>
              <input list="techpages-industries" value={f.industry} onChange={e => set('industry')(e.target.value)}
                placeholder="Industry…" title="Major industry category (pick one or type)"
                className="h-9 px-3 rounded-md border-[0.5px] border-primary-300 bg-white text-sm w-56" />
              <datalist id="techpages-industries">
                {KNOWN_INDUSTRIES.map(i => <option key={i} value={i} />)}
              </datalist>
              <Select value={f.trl} onChange={set('trl')} options={TRL_OPTS}
                labels={{ '': 'Any TRL', ...Object.fromEntries(TRL_OPTS.slice(1).map(t => [t, `TRL ${t}`])) }} />
            </>
          )}
          <Select value={f.state} onChange={set('state')} options={['', ...STATES]}
            labels={{ '': 'Any visitor state', ...Object.fromEntries(STATES.map(s => [s, `Viewed ${STATE_LABELS[s].toLowerCase()}`])) }} />
          <Select value={f.source} onChange={set('source')} options={['', ...SOURCES]}
            labels={{ '': 'Any source', ...SOURCE_LABELS }} />
          <Select value={f.has_traffic} onChange={set('has_traffic')} options={['', '1', '0']}
            labels={{ '': 'With or without views', '1': 'Only pages with views', '0': 'Only pages with no views' }} />
        </div>
        <div className="flex items-center gap-3 flex-wrap text-xs text-grayscale-1200">
          <span className="text-grayscale-1100">Metric columns</span>
          {dynCols.map(g => (
            <label key={g.key} className="inline-flex items-center gap-1.5 cursor-pointer whitespace-nowrap">
              <input type="checkbox" checked={!!show[g.key]} onChange={e => setShow(s => ({ ...s, [g.key]: e.target.checked }))} />
              {g.title}{g.keys.length ? ` (${g.keys.length})` : ''}
            </label>
          ))}
        </div>
        {chips.length > 0 && (
          <div className="flex gap-1.5 flex-wrap">
            {chips.map(c => <Chip key={c.label} label={c.label} value={c.value} onClear={c.clear} />)}
          </div>
        )}
        {dlErr && <Msg kind="error">Export failed: {dlErr}</Msg>}
        {res.error && <Msg kind="error">{res.error}</Msg>}
        {res.loading && !res.data ? <Loading label="Counting page traffic…" /> : (
          <DataTable rows={rows} columns={columns} labels={labels} maxHeight="44rem"
            empty={chips.length ? 'No pages match these filters.' : 'No pages yet — rebuild the URLs under "URLs & SEO".'}
            serverSort={sort} onSortChange={(key, dir) => setSort(key && dir ? { key, dir } : null)}
            unsortable={['horizon']}
            render={{
              title: r => (
                <span className="flex flex-col leading-tight max-w-[22rem]">
                  <button type="button" onClick={() => openRow(r)} title="Open this page's detail"
                    className="text-left font-medium text-grayscale-1300 underline underline-offset-2 truncate">
                    {str(r.title) || str(r.target)}
                  </button>
                  <span className="text-[10px] font-mono text-grayscale-1100 truncate" title={str(r.path)}>{str(r.path) || str(r.target)}</span>
                </span>
              ),
              university_label: r => str(r.university_label) || '—',
              trl: r => (r.trl === null || r.trl === undefined || r.trl === '' ? '—' : `TRL ${r.trl}`),
              horizon: r => <HorizonBadge h={r.horizon} techId={str(r.target)} />,
              views: r => <span className="tabular-nums font-medium">{fmtInt(r.views)}</span>,
              visitors: r => <span className="tabular-nums">{fmtInt(r.visitors)}</span>,
              avg_seconds: r => <span className="tabular-nums">{fmtSeconds(r.avg_seconds)}</span>,
              signups: r => <span className="tabular-nums">{fmtInt(r.signups)}</span>,
              crawler_hits: r => <span className="tabular-nums">{fmtInt(r.crawler_hits)}</span>,
              last_view_at: r => <span className="text-xs">{r.last_view_at ? fmtDate(r.last_view_at) : '—'}</span>,
            }}
            actions={r => (
              <span className="inline-flex items-center gap-2">
                <Btn small onClick={() => openRow(r)}>Details</Btn>
                <OpenLink path={r.path} />
              </span>
            )} />
        )}
        {pages > 1 && (
          <Pager page={page} pages={pages} total={total} shown={raw.length} per={per}
            onPage={setPage} perPage={perPage} onPerPage={setPerPage} />
        )}
      </Section>
      {open && <PageDrawer page={open} range={range} onClose={() => setOpen(null)} />}
    </div>
  );
};

export default PagesTable;
